import { SortableContext, useSortable } from "@dnd-kit/sortable"
import WeeklyObjectivesCardSortable from "./weekly-objectives-card-sortable"
import type {
  WeeklyObjective,
  WeeklyOfWeekly as Column,
} from "../../entities/weekly-objectives/weekly-objectives"
import {
  addDays,
  format,
  intervalToDuration,
  isBefore,
  isToday,
  isWithinInterval,
  toDate,
} from "date-fns"
import { cn } from "@/lib/utils"
import { formatDuration } from "@/shared/utils/date-fn"
import { useState } from "react"

function sortObjectives(objectives: WeeklyObjective[]): WeeklyObjective[] {
  // Group objectives by subjectId
  const grouped = objectives.reduce(
    (acc, objective) => {
      if (!acc[objective.subjectId]) {
        acc[objective.subjectId] = []
      }
      acc[objective.subjectId].push(objective)
      return acc
    },
    {} as Record<string, WeeklyObjective[]>,
  )

  // Sort groups by subjectIndex
  const sortedGroups = Object.values(grouped).sort((a, b) => a[0].subjectIndex - b[0].subjectIndex)

  // Separate approved and non-approved objectives
  const flattenedObjectives = sortedGroups.flat()

  const nonApprovedObjectives = flattenedObjectives.filter(
    (objective) => objective.status !== "approved",
  )

  const approvedObjectives = flattenedObjectives.filter(
    (objective) => objective.status === "approved",
  )

  // Combine non-approved and approved objectives
  return [...nonApprovedObjectives, ...approvedObjectives]
}

interface WeeklyObjectivesColumnProps {
  index: number
  column: Column
  isActive: boolean
  totalSeconds: number
  startDate: string
  endDate: string
  displayObjective?: (column: Column, id: string) => () => void
  isDragging: boolean
  isPending: boolean
  sourceColumn: Column | null
  realDurationInHs: number
  isOverloaded: boolean
}

export default function WeeklyObjectivesColumn({
  index,
  column,
  isActive,
  totalSeconds,
  displayObjective,
  startDate,
  endDate,
  isDragging,
  isPending,
  sourceColumn,
  realDurationInHs,
  isOverloaded,
}: WeeklyObjectivesColumnProps) {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false)

  const { setNodeRef } = useSortable({
    id: `column-${column.id}`,
    data: {
      type: "column",
      column,
    },
  })

  const start = new Date(column.startDate)
  const end = new Date(column.endDate)
  const today = new Date()

  const isAnOldColumn =
    isBefore(start, today) && isBefore(end, today) && !isToday(start) && !isToday(end)

  const startEndWeekDays = `${format(addDays(startDate, 1), "d")} – ${format(addDays(endDate, 1), "d MMM")}`

  const isCurrentWeek = isWithinInterval(new Date(), {
    start: toDate(startDate),
    end: toDate(endDate),
  })

  const duration = intervalToDuration({
    start: 0,
    end: totalSeconds * 1000,
  })

  const formattedDuration = isAnOldColumn
    ? realDurationInHs > 0
      ? `${realDurationInHs} h`
      : ""
    : formatDuration(duration).min({
        days: true,
        hours: true,
        minutes: true,
        seconds: false,
      })

  const sortedColumns: WeeklyObjective[] = sortObjectives(column.objectives)

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "min-h-[500px] w-full select-none rounded-[10px] p-3 md:w-[300px]",
        isCurrentWeek ? "!bg-neutral-200 !shadow" : "bg-neutral-100",
        isActive ? "border-2 border-primary bg-primary-50" : "bg-neutral-100",
        isDragging && !isActive
          ? "border-2 border-primary"
          : !isActive && "border-2 border-transparent",
        isAnOldColumn &&
          isDragging &&
          "pointer-events-none border-transparent bg-neutral-100 opacity-50",
      )}
    >
      <div className="mb-10 flex w-full items-center justify-between px-1">
        <div className="flex flex-col">
          <p
            className={cn(
              "text-md flex items-center gap-2 font-normal",
              isDragging && "text-primary",
              !isDragging && "text-neutral-700",
            )}
          >
            {isCurrentWeek ? (
              <span className="inline-flex items-center gap-2">
                <span className={cn("font-medium", "text-primary")}>Semana</span>
                <span
                  className={cn(
                    "flex h-8 items-center justify-center",
                    "w-8 rounded-full bg-primary text-sm font-bold text-white",
                  )}
                >
                  {index}
                </span>
              </span>
            ) : (
              <span
                className={cn(
                  "inline-flex h-8 font-medium",
                  isAnOldColumn && "text-neutral-400 line-through",
                )}
              >
                Semana {index}
              </span>
            )}
          </p>
          <p className="text-xs font-normal text-neutral-400">{startEndWeekDays}</p>
        </div>
        {formattedDuration ? (
          <div
            className="relative inline-block"
            onMouseEnter={() => setIsTooltipVisible(true)}
            onMouseLeave={() => setIsTooltipVisible(false)}
          >
            <p
              className={cn(
                "text-md font-normal",
                isAnOldColumn ? "text-neutral-400" : "text-neutral-600",
                isOverloaded && "flex h-fit items-center justify-center gap-2 text-destructive",
              )}
            >
              {isOverloaded && (
                <span className="inline-block h-2.5 w-2.5 animate-pulse-shadow rounded-full bg-destructive"></span>
              )}
              {formattedDuration}
            </p>

            {isTooltipVisible && isOverloaded && (
              <div className="absolute right-[80px] top-[65px] z-10 w-[200px] -translate-y-full transform overflow-hidden rounded-lg border border-neutral-200 bg-white p-2 text-xs shadow-lg">
                <p>Superaste las horas de estudio declaradas. Ajusta tus horas en Mi agenda.</p>
              </div>
            )}
          </div>
        ) : null}
      </div>
      <div className="relative top-[-15px] flex flex-col space-y-2">
        <SortableContext items={sortedColumns.map((obj) => obj.id)} strategy={() => null}>
          {sortedColumns.map((weeklyObjective) => (
            <WeeklyObjectivesCardSortable
              key={weeklyObjective.id}
              weeklyObjective={weeklyObjective}
              displayObjective={displayObjective?.(column, weeklyObjective.id)}
              areDragging={isDragging}
              isPending={isPending}
              sourceColumn={sourceColumn}
              isAnOldColumn={isAnOldColumn}
            />
          ))}
        </SortableContext>
      </div>
    </div>
  )
}
